import { hasModifyAccessToResource } from "api/general";
import { useLeaderboards } from "api/leaderboards";
import { useAppDispatch, useAppSelector } from "app/hooks";
import {
  DynamicSearch,
  DynamicSearchType,
} from "components/FiltersV2/DynamicSearch";
import { FiltersV2 } from "components/FiltersV2/Filters";
import {
  getNewFiltersOnly,
  parseFiltersForBE,
} from "components/FiltersV2/utils";
import { Subheader } from "components/Header/Subheader/Subheader";
import { SubheaderMenuButton } from "components/Header/Subheader/SubheaderMenuButton";
import { SubheaderWrapper } from "components/Header/Subheader/SubheaderWrapper";
import { TopHeader } from "components/Header/TopHeader/TopHeader";
import { createHeadItems, OLTable } from "components/Table/OLTable";
import { calculateCount } from "components/Table/tableHelpers";
import { Tooltip } from "components/Tooltips/Tooltip";
import { useMessage } from "hooks/intl";
import { useListing } from "hooks/listing";
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { clearTablePage, selectTables } from "reduxState/tables/tablesSlice";
import { useTablesStateHelpers } from "state/tables/tableHelpers";
import { LEADERBOARD_LIST_ID } from "state/tables/types";
import { CampaignTriggers } from "types/campaigns";
import { SelectedFilter } from "types/filters";
import { LeaderboardBE } from "types/leaderboards/base";
import { ListingActions } from "types/listing";
import { OLDate } from "utilities/dateFormat";
import { links } from "utilities/links";
import { trackedMetrics } from "../utils";

export function LeaderboardsList() {
  const navigate = useNavigate();
  const { message } = useMessage();
  const { listing, updateListing } = useListing(LEADERBOARD_LIST_ID);
  const appDispatch = useAppDispatch();
  const tables = useAppSelector(selectTables);
  const {
    setPersistentFilters,
    setPersistentListingSorting,
    setColumnOrdering,
  } = useTablesStateHelpers();
  const { useLeaderboardsList } = useLeaderboards();

  const hasModifyLeaderboardPermission = hasModifyAccessToResource("CAMPAIGN");

  const [filters, setFilters] = useState<SelectedFilter[]>(
    tables[LEADERBOARD_LIST_ID]?.filters?.filter(
      (item: SelectedFilter) => item.type !== "dynamic-search",
    ) || [],
  );
  const [dynamicSearch, setDynamicSearch] = useState<DynamicSearchType>();
  const { data: leaderboardListData, error } = useLeaderboardsList({
    filters: [
      ...parseFiltersForBE(filters),
      {
        query: "trigger",
        value: CampaignTriggers.LEADERBOARD,
        condition: "eq",
      },
    ],
    order: listing.order || "asc",
    page: listing.page,
    rows: listing.rowsPerPage,
    sortKey: listing.property || "name",
    dynamicSearch: dynamicSearch,
  });

  useEffect(() => {
    const isError = typeof error !== "undefined";
    // Save filters only when data is correct and error not occurs
    if (!isError && leaderboardListData) {
      setPersistentFilters(filters, LEADERBOARD_LIST_ID);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, leaderboardListData, error]);

  const setSelectedFilters = (filters: SelectedFilter[]) => {
    listing.page = 1;
    setPersistentListingSorting(
      listing.order,
      listing.property,
      listing.rowsPerPage,
      1,
      LEADERBOARD_LIST_ID,
    );
    setFilters(filters);
  };

  const headItems = createHeadItems([
    {
      headerText: message({ id: "leaderboardName" }),
      property: "name",
      isSortable: true,
    },
    {
      headerText: message({ id: "trackedMetric" }),
      property: "metric",
      isSortable: false,
    },
    {
      headerText: message({ id: "createdOn" }),
      property: "createdAt",
      isSortable: true,
    },
  ]);

  const createItems = useCallback(
    (items: LeaderboardBE[]) => {
      return items.map((item) => {
        return {
          cells: [
            {
              content: (
                <Tooltip text={item.name} placement="bottom-end">
                  <span>{item.name}</span>
                </Tooltip>
              ),
              property: "name",
            },
            {
              content: message({ id: trackedMetrics[0].label }),
              property: "metric",
            },
            {
              content: OLDate(item.createdAt),
              property: "createdAt",
            },
          ],
          id: item.campaignId,
          data: item,
        };
      });
    },
    [message],
  );

  const items = leaderboardListData?.items
    ? createItems(leaderboardListData.items)
    : [];

  return (
    <>
      <TopHeader title={message({ id: "leaderboards" })} />
      <Subheader>
        <FiltersV2
          setFilters={setSelectedFilters}
          savedFilters={getNewFiltersOnly(filters)}
          properties={[
            {
              query: "active",
              label: message({ id: "status" }),
              type: "boolean-status",
            },
            {
              query: "labels",
              label: message({ id: "customAttributes" }),
              type: "multiwithmodal",
            },
            {
              query: "startsAt",
              label: message({ id: "activeFrom" }),
              type: "datetime",
            },
            {
              query: "endsAt",
              label: message({ id: "activeTo" }),
              type: "datetime",
            },
          ]}
        />
        <SubheaderMenuButton
          text={message({ id: "addLeaderboard" })}
          menuItems={[
            {
              text: message({ id: "addLeaderboard" }),
              onClick: () => navigate(links.addLeaderboard),
            },
          ]}
          data-test-id="leaderboards-add"
        />
      </Subheader>
      <Subheader>
        <SubheaderWrapper alignRight>
          <DynamicSearch
            property="name"
            setDynamicSearch={setDynamicSearch}
            onSearch={() =>
              appDispatch(clearTablePage({ tableName: LEADERBOARD_LIST_ID }))
            }
          />
        </SubheaderWrapper>
      </Subheader>
      <OLTable
        fullView
        columns={leaderboardListData && headItems}
        rows={leaderboardListData && items}
        onSort={(prop) =>
          updateListing({
            payload: prop,
            type: ListingActions.SET_SORT,
          })
        }
        sort={{
          order: listing.order || "asc",
          property: listing.property || "name",
          page: listing.page,
          rowsPerPage: listing.rowsPerPage,
        }}
        actions={{
          body: [
            {
              action: (id) => navigate(links.editLeaderboard(id as string)),
              disableCondition: () => !hasModifyLeaderboardPermission,
              label: message({ id: "editLeaderboard" }),
            },
          ],
        }}
        tableName={LEADERBOARD_LIST_ID}
        setColumnOrdering={setColumnOrdering}
        onRowClick={(id) => navigate(links.leaderboard(id))}
        pagination={{
          count: calculateCount(
            leaderboardListData?.total.filtered,
            leaderboardListData?.total.all,
          ),
          rowsPerPage: listing.rowsPerPage,
          page: listing.page,
          onChangePage: (page) => {
            updateListing({ type: ListingActions.CHANGE_PAGE, payload: page });
          },
          onChangeRowsPerPage: (rows) => {
            updateListing({
              type: ListingActions.CHANGE_ROWS_PER_PAGE,
              payload: rows,
            });
          },
        }}
      />
    </>
  );
}
